import { useEffect, useMemo, useRef, useState } from 'react';
import { createGeneration, getPreview, getRemainingUsage } from '../lib/api';
import {
  clothingOptions,
  demoImages,
  moodOptions,
  personCountOptions,
  petCountOptionsByPeople,
  processingSlides,
  sampleAsset,
} from '../lib/samples';
import { ChoiceCard } from '../components/ChoiceCard';
import { FileDrop } from '../components/FileDrop';
import { Reviews } from '../components/Reviews';
import { getClientId } from '../lib/usage';
import { getConfig } from '../lib/config';
import { readActiveRoot, rememberActiveRoot } from '../lib/active-root';
import { estimatePortraitProgress, PortraitProgressStage } from '../lib/progress';

async function canLoadImage(url: string) {
  return new Promise<boolean>((resolve) => {
    const image = new Image();
    const timer = window.setTimeout(() => {
      image.onload = null;
      image.onerror = null;
      resolve(false);
    }, 8000);

    image.onload = () => {
      window.clearTimeout(timer);
      resolve(true);
    };
    image.onerror = () => {
      window.clearTimeout(timer);
      resolve(false);
    };
    image.src = url;
  });
}

function ProcessingView({ message, progress }: { message: string; progress: number }) {
  const [activeStory, setActiveStory] = useState(0);

  const goToStory = (index: number) => {
    const nextIndex = (index + processingSlides.length) % processingSlides.length;
    setActiveStory(nextIndex);
  };

  return (
    <div className="upload-flow processing-flow">
      <header className="hero-copy">
        <h1>
          Together in a Moment
          <br />
          That Lasts <em>Forever</em>
        </h1>
      </header>

      <div className="processing-box">
        <div className="processing-copy">
          <span>{message}</span>
        </div>
        <div className="progress-track" aria-label="Portrait generation progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} role="progressbar">
          <span style={{ width: `${progress}%` }} />
        </div>
        <strong className="processing-percent">{progress}%</strong>
        <small className="processing-remaining">We will keep checking until your portrait is ready.</small>
      </div>

      <p className="processing-note">
        Your portrait is being prepared! Please keep this page open while it comes to life
      </p>

      <section className="processing-slider-section">
        <div className="processing-slider">
          <button
            type="button"
            className="processing-slider-nav processing-slider-prev"
            aria-label="Previous testimonial"
            onClick={() => goToStory(activeStory - 1)}
          >
            <span aria-hidden="true">&lsaquo;</span>
          </button>
          <div className="processing-slider-viewport">
            <div className="processing-slider-track" style={{ transform: `translateX(-${activeStory * 100}%)` }}>
              {processingSlides.map((slide) => (
                <article className="processing-slide" key={slide.author}>
                  <div className="processing-slide-copy">
                    <div className="processing-stars" aria-label="5 star review">
                      <span>★</span>
                      <span>★</span>
                      <span>★</span>
                      <span>★</span>
                      <span>★</span>
                    </div>
                    <p>"{slide.text}"</p>
                    <strong>– {slide.author}</strong>
                  </div>
                  <div className="processing-slide-image">
                    <img alt={slide.author} src={slide.image} loading="lazy" />
                  </div>
                </article>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="processing-slider-nav processing-slider-next"
            aria-label="Next testimonial"
            onClick={() => goToStory(activeStory + 1)}
          >
            <span aria-hidden="true">&rsaquo;</span>
          </button>
          <div className="processing-slider-pagination" aria-hidden="true">
            {processingSlides.map((slide, index) => (
              <span className={index === activeStory ? 'is-active' : ''} key={slide.author} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function previewPath(id: string, token?: string) {
  const params = new URLSearchParams({ preview: id });
  if (token) params.set('token', token);
  return `/?${params.toString()}`;
}

function rememberAccessToken(id: string, token?: string) {
  if (!id || !token) return;
  window.sessionStorage.setItem(`portrait_access_token:${id}`, token);
}

function readUploadRoot() {
  return new URLSearchParams(window.location.search).get('uploadRoot') || readActiveRoot()?.rootId || '';
}

function readUploadRootToken(rootId: string) {
  if (!rootId) return '';
  return (
    new URLSearchParams(window.location.search).get('token') ||
    window.sessionStorage.getItem(`portrait_access_token:${rootId}`) ||
    readActiveRoot()?.token ||
    ''
  );
}

function DownloadView({
  error,
  message,
  onDownloadAgain
}: {
  error?: string;
  message: string;
  onDownloadAgain: () => void;
}) {
  return (
    <div className="download-flow">
      <div className="download-card">
        <span className="download-kicker">Portrait download</span>
        <h1>Your portrait file</h1>
        <p>{message}</p>
        {error && <p className="form-error">{error}</p>}
        <button type="button" className="primary-action" onClick={onDownloadAgain}>
          Download again
        </button>
      </div>
    </div>
  );
}

export function UploadPage() {
  const downloadId = useMemo(() => new URLSearchParams(window.location.search).get('download'), []);
  const uploadRootId = useMemo(readUploadRoot, []);
  const uploadRootToken = useMemo(() => readUploadRootToken(uploadRootId), [uploadRootId]);
  const [humanImages, setHumanImages] = useState<File[]>([]);
  const [petImages, setPetImages] = useState<File[]>([]);
  const [mood, setMood] = useState(moodOptions[0].id);
  const [numberOfPersons, setNumberOfPersons] = useState(1);
  const [numberOfPets, setNumberOfPets] = useState(1);
  const [clothingStyle] = useState(clothingOptions[0].id);
  const [isSubmitting, setSubmitting] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingStage, setProcessingStage] = useState<PortraitProgressStage>('analyzing');
  const [processingStartedAt, setProcessingStartedAt] = useState<number>();
  const [progressTick, setProgressTick] = useState(0);
  const [error, setError] = useState('');
  const [downloadMessage, setDownloadMessage] = useState('');
  const [remainingUses, setRemainingUses] = useState<number>();
  const demoStripRef = useRef<HTMLDivElement>(null);
  const config = getConfig();
  const clientId = useMemo(() => getClientId(), []);
  const moodIndex = Math.max(0, moodOptions.findIndex((option) => option.id === mood));
  const petCountOptions = petCountOptionsByPeople[numberOfPersons] || petCountOptionsByPeople[1];

  const hasRemainingUses = remainingUses === undefined || remainingUses > 0;
  const canSubmit = useMemo(
    () =>
      Boolean(
        humanImages.length === numberOfPersons &&
          petImages.length === numberOfPets &&
          !isSubmitting &&
          hasRemainingUses
      ),
    [hasRemainingUses, humanImages.length, isSubmitting, numberOfPersons, numberOfPets, petImages.length]
  );

  function setHumanImageAt(index: number, file: File) {
    setHumanImages((current) => {
      const next = current.slice(0, numberOfPersons);
      next[index] = file;
      return next;
    });
  }

  function setPetImageAt(index: number, file: File) {
    setPetImages((current) => {
      const next = current.slice(0, numberOfPets);
      next[index] = file;
      return next;
    });
  }

  useEffect(() => {
    setHumanImages((current) => current.slice(0, numberOfPersons));
  }, [numberOfPersons]);

  useEffect(() => {
    setPetImages((current) => current.slice(0, numberOfPets));
  }, [numberOfPets]);

  useEffect(() => {
    if (!isSubmitting || !processingStartedAt) return;
    const timer = window.setInterval(() => setProgressTick((tick) => tick + 1), 1000);
    return () => window.clearInterval(timer);
  }, [isSubmitting, processingStartedAt]);

  useEffect(() => {
    if (!isSubmitting || !processingStartedAt) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isSubmitting, processingStartedAt]);

  useEffect(() => {
    let active = true;
    getRemainingUsage(clientId)
      .then((usage) => {
        if (active) setRemainingUses(usage.remaining);
      })
      .catch(() => {
        if (active) setRemainingUses(undefined);
      });

    return () => {
      active = false;
    };
  }, [clientId]);

  async function downloadFinalImage(force = false) {
    if (!downloadId) return;
    const downloadKey = `portrait_download_started:${downloadId}`;
    if (!force && window.sessionStorage.getItem(downloadKey)) {
      setDownloadMessage('Your download was already started. Use the button below if you need to download the file again.');
      return;
    }

    setDownloadMessage('Preparing your portrait download...');
    setError('');

    try {
      const downloadToken = new URLSearchParams(window.location.search).get('token') || window.sessionStorage.getItem(`portrait_access_token:${downloadId}`) || '';
      const preview = await getPreview(downloadId, downloadToken);
      if (!preview.downloadUrl) {
        throw new Error('Final image is not ready yet. Please try again in a moment.');
      }

      await downloadImage(preview.downloadUrl, `portrait-${downloadId}.jpg`);
      window.sessionStorage.setItem(downloadKey, '1');
      setDownloadMessage('Your download has started. You can close this page after the file is saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not download your portrait.');
      setDownloadMessage('We could not start the download automatically.');
    }
  }

  useEffect(() => {
    if (!downloadId) return;
    void downloadFinalImage();
  }, [downloadId]);

  async function submit() {
    if (humanImages.length !== numberOfPersons || petImages.length !== numberOfPets) return;

    setError('');
    setSubmitting(true);
    setProcessingStartedAt(Date.now());
    setProgressTick(0);
    setProcessingStage('analyzing');
    setProcessingMessage('Analyzing your photos...');

    try {
      const result = await createGeneration({
        humanImages,
        petImages,
        mood,
        clothingStyle,
        numberOfPersons,
        numberOfPets,
        clientId,
        rootId: uploadRootId,
        accessToken: uploadRootToken,
        trademark: config.trademark
      });
      if (typeof result.remainingUses === 'number') {
        setRemainingUses(result.remainingUses);
      }
      rememberAccessToken(result.id, result.accessToken);
      rememberAccessToken(result.rootId || result.id, result.accessToken);
      rememberActiveRoot(result.rootId || result.id, result.accessToken);
      setProcessingStage('queued');
      setProcessingMessage('Queued for portrait generation...');

      for (let attempt = 0; attempt < 45; attempt += 1) {
        const preview = await getPreview(result.id, result.accessToken);
        const imageReady = preview.previewUrl ? await canLoadImage(preview.previewUrl) : false;
        if (preview.status === 'queued') {
          setProcessingStage('queued');
          setProcessingMessage('Queued for portrait generation...');
        }
        if (preview.status === 'processing') {
          setProcessingStage(preview.previewUrl ? 'finalizing' : 'processing');
          setProcessingMessage(preview.previewUrl ? 'Finalizing your preview image...' : 'Creating your portrait...');
        }
        if (preview.status === 'completed') {
          if (!imageReady) {
            setProcessingStage('finalizing');
            setProcessingMessage('Finalizing your preview image...');
            await new Promise((resolve) => window.setTimeout(resolve, 1000));
            continue;
          }
          setProcessingStage('completed');
          window.location.href = previewPath(result.id, result.accessToken);
          return;
        }
        if (preview.status === 'failed') {
          setProcessingStage('failed');
          throw new Error(preview.errorMessage || 'Generation failed.');
        }
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
      }

      window.location.href = previewPath(result.id, result.accessToken);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your preview.');
      setSubmitting(false);
      setProcessingMessage('');
      setProcessingStage('analyzing');
      setProcessingStartedAt(undefined);
    }
  }

  function scrollDemo(direction: -1 | 1) {
    const strip = demoStripRef.current;
    if (!strip) return;
    const gap = Number.parseFloat(window.getComputedStyle(strip).columnGap || '0') || 0;
    strip.scrollBy({ left: direction * (strip.clientWidth + gap), behavior: 'smooth' });
  }

  if (isSubmitting && processingMessage) {
    const progress = estimatePortraitProgress(processingStartedAt, processingStage, progressTick);
    return <ProcessingView message={processingMessage} progress={progress} />;
  }

  if (downloadId) {
    return (
      <DownloadView
        error={error}
        message={downloadMessage || 'Preparing your portrait download...'}
        onDownloadAgain={() => void downloadFinalImage(true)}
      />
    );
  }

  return (
    <div className="upload-flow">
      <header className="hero-copy">
        <picture>
          <source media="(max-width: 760px)" srcSet={sampleAsset('soulpaw-Anh_bia_moblie.jpg')} />
          <img
            alt="Soulpaw family portrait with people and pets"
            className="hero-sample"
            src={sampleAsset('soulpaw-Anh_bia_desktop.jpg')}
          />
        </picture>
      </header>

      <section className="step-section">
        <h2>
          <span className="step-number">1</span>
          <span className="step-title">
            <em>Choose</em> Your Favorite Concept
          </span>
        </h2>
        <div className="choice-grid mood-grid">
          {moodOptions.map((option) => (
            <ChoiceCard
              image={option.image}
              key={option.id}
              label={option.label}
              onClick={() => setMood(option.id)}
              selected={mood === option.id}
            />
          ))}
        </div>
      </section>

      <section className="step-section">
        <h2>
          <span className="step-number">2</span>
          <span className="step-title">
            <em>Choose</em> The Number Of Person In The Portrait
          </span>
        </h2>
        <div className="choice-grid count-grid">
          {personCountOptions.map((option) => (
            <ChoiceCard
              image={option.imageByMood[moodIndex] || option.imageByMood[0]}
              key={option.count}
              label={option.label}
              onClick={() => setNumberOfPersons(option.count)}
              selected={numberOfPersons === option.count}
            />
          ))}
        </div>
      </section>

      <section className="step-section">
        <h2>
          <span className="step-number">3</span>
          <span className="step-title">
            <em>Choose</em> The Number Of Pets In The Portrait
          </span>
        </h2>
        <div className="choice-grid count-grid">
          {petCountOptions.map((option) => (
            <ChoiceCard
              image={option.imageByMood[moodIndex] || option.imageByMood[0]}
              key={option.count}
              label={option.label}
              onClick={() => setNumberOfPets(option.count)}
              selected={numberOfPets === option.count}
            />
          ))}
        </div>
      </section>

      <section className="step-section">
        <h2>
          <span className="step-number">4</span>
          <span className="step-title">
            <em>Upload</em> Your Photos
          </span>
        </h2>
        <div className="upload-groups">
          <div className="upload-group-card">
            <h3>Upload the Person&apos;s Photo:</h3>
            <div className={`upload-drop-grid ${numberOfPersons === 1 ? 'is-single' : ''}`}>
              {Array.from({ length: numberOfPersons }).map((_, index) => (
                <FileDrop
                  key={`person-${index}`}
                  label={`Photo of Person ${index + 1}`}
                  value={humanImages[index]}
                  onChange={(file) => setHumanImageAt(index, file)}
                />
              ))}
            </div>
          </div>
          <div className="upload-group-card">
            <h3>Upload the Pet&apos;s Photo:</h3>
            <div className={`upload-drop-grid ${numberOfPets === 1 ? 'is-single' : ''}`}>
              {Array.from({ length: numberOfPets }).map((_, index) => (
                <FileDrop
                  key={`pet-${index}`}
                  label={`Photo of Pet ${index + 1}`}
                  value={petImages[index]}
                  onChange={(file) => setPetImageAt(index, file)}
                />
              ))}
            </div>
          </div>
        </div>
        <p className="hint">*For the best results, please upload a clear photo of you and your pet</p>
      </section>

      {error && <p className="form-error">{error}</p>}
      <p className={`usage-status ${hasRemainingUses ? '' : 'is-empty'}`}>
        {remainingUses === undefined
          ? 'You can view up to 3 demos per day.'
          : remainingUses > 0
            ? `${remainingUses} demo ${remainingUses === 1 ? 'view' : 'views'} remaining today.`
            : 'You have used all 3 demo views today. Please come back tomorrow.'}
      </p>
      <button className={`primary-action ${canSubmit ? 'is-active' : ''}`} disabled={!canSubmit} type="button" onClick={submit}>
        {isSubmitting ? 'Creating Your Demo...' : hasRemainingUses ? 'View Demo' : 'Daily Demo Limit Reached'}
      </button>

      <div className="demo-carousel">
        <button className="demo-nav demo-nav-prev" type="button" aria-label="Previous demo" onClick={() => scrollDemo(-1)}>
          ‹
        </button>
        <div className="demo-strip" ref={demoStripRef}>
          {demoImages.map((image) => (
            <img alt="" key={image} src={image} />
          ))}
        </div>
        <button className="demo-nav demo-nav-next" type="button" aria-label="Next demo" onClick={() => scrollDemo(1)}>
          ›
        </button>
      </div>

      <Reviews config={config.reviews} variant="summary" />
    </div>
  );
}

async function downloadImage(url: string, filename: string) {
  try {
    const response = await fetch(url);
    if (!response.ok || !(response.headers.get('content-type') || '').startsWith('image/')) {
      throw new Error('Could not download the final image.');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1200);
  } catch {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
}
